import 'react-table/react-table.css';

import ReactTable, {
  type Column,
  type ComponentPropsGetter0,
  type ComponentPropsGetterC,
  type ComponentPropsGetterR,
  type ComponentPropsGetterRC,
  type SortingRule,
  type SubComponentFunction,
  type TableProps as ReactTableProps,
} from 'react-table';

import React from 'react';
import classNames from 'classnames';

import Empty from 'components/common/empty/Empty';
import Loading from 'components/common/loading/Loading';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
type DefaultRow = { [key: string]: JsonValue };

type Props<D extends object = DefaultRow> = {
  style?: React.CSSProperties;
  columns?: Array<Column<D>>;
  loading?: boolean;
  minRows?: number;
  sortable?: boolean;
  expanded?: ReactTableProps<D>['expanded'];
  data?: D[];
  noDataText?: string;
  getTdProps?: ComponentPropsGetterRC | ComponentPropsGetterR;
  getTrProps?: ComponentPropsGetterR | ComponentPropsGetter0;
  subComponent?: SubComponentFunction;
  getTbodyProps?: ComponentPropsGetter0;
  defaultSorted?: SortingRule[];
  showPagination?: boolean;
  getTheadThProps?: ComponentPropsGetterC;
  customClassName?: string;
  noDataClassName?: string;
};

function Table<D extends object = DefaultRow>({
  style,
  columns,
  loading = false,
  minRows,
  sortable = true,
  expanded,
  data = [],
  noDataText,
  getTdProps,
  getTrProps,
  subComponent,
  getTbodyProps,
  defaultSorted,
  showPagination,
  getTheadThProps,
  customClassName,
  noDataClassName,
}: Props<D>) {
  const resolvedMinRows = minRows ?? data.length;

  const NoDataComponent = () => {
    return loading ? (
      <div className="loading__container"></div>
    ) : (
      <Empty message={noDataText ? noDataText : 'No data to show.'} className={noDataClassName} />
    );
  };

  return (
    <ReactTable<D>
      data={data}
      NoDataComponent={NoDataComponent}
      noDataText={loading ? '' : noDataText}
      className={classNames('lf', customClassName)}
      columns={columns}
      loading={loading}
      minRows={resolvedMinRows}
      pageSize={resolvedMinRows}
      sortable={sortable}
      getTrProps={getTrProps}
      getTheadThProps={getTheadThProps}
      getTbodyProps={getTbodyProps}
      getTdProps={getTdProps}
      showPagination={showPagination}
      defaultSorted={defaultSorted}
      loadingText={<Loading />}
      style={style}
      expanded={expanded}
      SubComponent={subComponent}
    />
  );
}

export default Table;
